import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { forumCategories, mockTopics, mockReplies, mockUsers } from '../data/forumData';
import NewTopicForm from '../components/NewTopicForm';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from "../components/SEOHead";
import { ThumbsUp } from 'lucide-react';

import { useTranslation } from 'react-i18next';
const FORUM_UPVOTES_KEY = 'maxemployee.forum.upvotes';

function loadUpvotes() {
try {
    const stored = localStorage.getItem(FORUM_UPVOTES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveUpvotes(upvotes) {
  try {
    localStorage.setItem(FORUM_UPVOTES_KEY, JSON.stringify(upvotes));
  } catch {}
}

function getTopicUpvotes(topicId, upvotes) {
  return upvotes[topicId] || 0;
}

const Forum = () => {
  const { t } = useTranslation();
  const { category } = useParams();
  const location = useLocation();
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [showNewTopic, setShowNewTopic] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('recent');
  const [upvotes, setUpvotes] = useState({});
  const navigate = useNavigate();

  // Load upvotes from localStorage on mount
  useEffect(() => {
    setUpvotes(loadUpvotes());
  }, []);

  // Check if we're on the /new sub-route
  useEffect(() => {
    setShowNewTopic(location.pathname === '/community/new');
  }, [location]);

  const handleUpvote = useCallback((topicId, e) => {
    if (e) e.preventDefault();
    setUpvotes(prev => {
      const current = prev[topicId] || 0;
      const next = { ...prev, [topicId]: current + 1 };
      saveUpvotes(next);
      return next;
    });
  }, []);

  useEffect(() => {
    let topics = [...mockTopics];
    let foundCategory = null;

    if (category) {
      foundCategory = forumCategories.find(cat => cat.id === category);
      if (foundCategory) {
        topics = mockTopics.filter(topic => topic.categoryId === category);
      } else {
        navigate('/community');
        return;
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      topics = topics.filter(
        t => t.title.toLowerCase().includes(q) || t.content.toLowerCase().includes(q)
      );
    }

    // Apply sorting/filtering
    switch (filter) {
      case 'popular':
        topics.sort((a, b) => (b.views + b.replies) - (a.views + a.replies));
        break;
      case 'upvoted':
        topics.sort((a, b) => {
          const aVotes = getTopicUpvotes(a.id, upvotes);
          const bVotes = getTopicUpvotes(b.id, upvotes);
          return bVotes - aVotes;
        });
        break;
      case 'unanswered':
        topics = topics.filter(t => t.replies === 0);
        break;
      case 'solved':
        topics = topics.filter(t => t.solved === true);
        break;
      case 'recent':
      default:
        topics.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    setFilteredTopics(topics);
    setCurrentCategory(foundCategory || { name: t('forum.all_topics'), description: t('forum.view_all_discussions') });
  }, [category, searchQuery, filter, navigate, upvotes]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleNewTopic = () => {
    navigate('/community/new');
  };

  const handleCancelNewTopic = () => {
    navigate(category ? `/community/${category}` : '/community');
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  if (showNewTopic) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10">
          <div className="text-sm text-zinc-500 mb-4 font-mono">
            <Link to="/community" className="hover:text-amber-500">{t('forum.community')}</Link>
            <span className="mx-2">/</span>
            <span>{t('forum.new_topic')}</span>
          </div>
          <NewTopicForm onCancel={handleCancelNewTopic} />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={t('forum.community_ai_agent_forum_max_employee')}
        description="Join the Max Employee community. Discuss AI agents, share workflows, ask questions, and connect with other AI practitioners."
        url="/community"
        noIndex={false}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
              { "@type": "ListItem", position: 2, name: "Community", item: "https://maxemployee.com/community" },
            ],
          },
        ]}
      />
      <Header />
      <main id="main-content">
        <section className="border-b border-zinc-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">{t('forum.breadcrumb_label')}</div>
            <h1 className="font-display text-2xl sm:text-4xl uppercase">{currentCategory?.name}</h1>
            <p className="text-zinc-400 mt-2 text-sm sm:text-base">{currentCategory?.description}</p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Sidebar: Categories and Filters */}
            <aside className="w-full lg:w-64 shrink-0">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3">{t('forum.categories')}</h3>
              <ul className="space-y-1">
                <li>
                  <Link
                    to="/community"
                    className={`block px-3 py-2 text-sm transition-colors min-h-[44px] flex items-center ${!category ? 'text-amber-500 bg-amber-500/10' : 'text-zinc-300 hover:text-amber-400 hover:bg-zinc-800'}`}
                  >
                    {t('forum.all_topics')}
                  </Link>
                </li>
                {forumCategories.map(cat => (
                  <li key={cat.id}>
                    <Link
                      to={`/community/${cat.id}`}
                      className={`block px-3 py-2 text-sm transition-colors min-h-[44px] flex items-center ${category === cat.id ? 'text-amber-500 bg-amber-500/10' : 'text-zinc-300 hover:text-amber-400 hover:bg-zinc-800'}`}
                    >
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Filters */}
              <div className="mt-6">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3">{t('forum.filters')}</h3>
                <div className="space-y-1">
                  {[
                    { key: 'recent', label: t('forum.recent') },
                    { key: 'popular', label: t('forum.popular') },
                    { key: 'upvoted', label: t('forum.most_upvoted') },
                    { key: 'unanswered', label: t('forum.unanswered') },
                    { key: 'solved', label: t('forum.solved') },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => handleFilterChange(f.key)}
                      className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors min-h-[44px] flex items-center ${
                        filter === f.key
                          ? 'bg-zinc-700 text-amber-500'
                          : 'text-zinc-300 hover:text-amber-400 hover:bg-zinc-800'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Topic Stats */}
              <div className="mt-6 p-3 bg-zinc-800/50 border border-zinc-800 rounded">
                <p className="text-zinc-400 text-xs font-mono">
                  <span className="text-amber-500 font-semibold">{mockTopics.length}</span> total topics
                </p>
                <p className="text-zinc-400 text-xs font-mono">
                  <span className="text-amber-500 font-semibold">{mockTopics.filter(t => t.solved).length}</span> solved
                </p>
                <p className="text-zinc-400 text-xs font-mono">
                  <span className="text-amber-500 font-semibold">{mockTopics.filter(t => t.replies === 0).length}</span> unanswered
                </p>
              </div>
            </aside>

            {/* Main Content: Topics List */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                <form onSubmit={handleSearch} role="search" aria-label={t('forum.search_forum_topics')} className="w-full sm:flex-grow">
                  <label htmlFor="forum-search" className="sr-only">{t('forum.search_topics')}</label>
                  <input
                    id="forum-search"
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('forum.search_topics')}
                    className="w-full p-2.5 rounded bg-zinc-800 text-white border border-zinc-700 focus:outline-none focus:border-amber-500 text-sm min-h-[44px]"
                  />
                </form>
                <button
                  onClick={handleNewTopic}
                  className="bg-amber-500 text-black px-4 py-2.5 rounded hover:bg-amber-400 transition-colors font-mono text-xs uppercase tracking-wider font-bold whitespace-nowrap min-h-[44px]"
                >
                  + New Topic
                </button>
              </div>

              {filteredTopics.length > 0 ? (
                <ul className="space-y-3">
                  {filteredTopics.map(topic => {
                    const topicCategory = forumCategories.find(c => c.id === topic.categoryId);
                    const topicAuthor = mockUsers.find(u => u.id === topic.author);
                    const topicUpvotes = getTopicUpvotes(topic.id, upvotes);
                    return (
                      <li key={topic.id} className="p-3 sm:p-4 rounded bg-zinc-800/50 border border-zinc-800 hover:border-zinc-700 transition-colors">
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-2">
                          <Link
                            to={`/community/topic/${topic.id}`}
                            className="text-sm sm:text-base font-semibold text-amber-500 hover:underline flex-1"
                          >
                            {topic.title}
                          </Link>
                          <div className="flex items-center gap-1.5 text-xs shrink-0 flex-wrap">
                            {topicCategory && (
                              <span
                                className="px-2 py-0.5 rounded-full text-[10px]"
                                style={{
                                  backgroundColor: getCategoryColor(topic.categoryId),
                                  color: 'white',
                                }}
                              >
                                {topicCategory.name}
                              </span>
                            )}
                            {topic.solved && (
                              <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px]">{t('forum.solved')}</span>
                            )}
                            {topic.views > 200 && (
                              <span className="px-2 py-0.5 rounded-full bg-rose-600 text-white text-[10px]">{t('forum.hot')}</span>
                            )}
                            {isNew(topic.createdAt) && (
                              <span className="px-2 py-0.5 rounded-full bg-amber-500 text-black text-[10px]">{t('forum.new')}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-zinc-400 text-xs flex flex-wrap items-center gap-x-2 gap-y-1">
                          <div className="flex items-center gap-1.5">
                            <img
                              src={topicAuthor?.avatar || '/path/to/default/avatar.png'}
                              alt={t('forum.author_avatar')}
                              className="w-4 h-4 rounded-full"
                              loading="lazy"
                              decoding="async"
                            />
                            <span>{topicAuthor?.name || topic.author}</span>
                          </div>
                          <span className="hidden sm:inline text-zinc-600">•</span>
                          <span>{timeAgo(topic.createdAt)}</span>
                          <span className="hidden sm:inline text-zinc-600">•</span>
                          <span className="text-zinc-500">
                            {topic.replies} replies · {topic.views} views
                          </span>
                          <button
                            onClick={(e) => handleUpvote(topic.id, e)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded transition-colors hover:bg-zinc-700 min-h-[32px]"
                            title={t('forum.upvote_this_topic')}
                          >
                            <ThumbsUp className={`w-3 h-3 ${topicUpvotes > 0 ? 'text-amber-500 fill-amber-500' : 'text-zinc-500'}`} />
                            <span className={`text-xs ${topicUpvotes > 0 ? 'text-amber-500' : 'text-zinc-500'}`}>
                              {topicUpvotes}
                            </span>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="p-8 text-center border border-zinc-800 rounded">
                  <p className="text-zinc-500 mb-4">{t('forum.no_topics_found_matching_your_criteria')}</p>
                  <button
                    onClick={() => { setSearchQuery(''); setFilter('recent'); }}
                    className="text-amber-500 hover:underline text-sm"
                  >
                    {t('forum.clear_filters')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// Helper functions
const categoryColors = {
  'getting-started': '#3b82f6',
  'tips-tricks': '#8b5cf6',
  'feature-requests': '#f59e0b',
  'integrations': '#10b981',
  'show-tell': '#ec4899',
  'bug-reports': '#ef4444',
};

function getCategoryColor(categoryId) {
  return categoryColors[categoryId] || '#6b7280';
}

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function isNew(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diffDays = (now - date) / (1000 * 60 * 60 * 24);
  return diffDays <= 2;
}

export default Forum;
